/**
 * Shared formula validation utility
 * Used by both validators.ts and useMathOperationsSection.ts
 */
export const validateFormulaSyntax = (
  formula: string,
  variables: { [key: string]: string }
): { isValid: boolean; error: string | null } => {
  const trimmed = formula.trim();

  // 1. Empty check
  if (!trimmed) {
    return { isValid: false, error: 'Please enter a formula' };
  }

  // 2. Invalid characters (allow A-Z, 0-9, operators, parentheses, decimal, spaces)
  const invalidChars = /[^A-Z0-9+\-*/().\s]/g;
  if (invalidChars.test(trimmed)) {
    return {
      isValid: false,
      error:
        'Invalid characters. Use only variables (A-Z), numbers, +, -, *, /, ( ), and decimal points.',
    };
  }

  // 3. Check for consecutive letters (variables must be separated by operators)
  const consecutiveLetters = /[A-Z]{2,}/;
  if (consecutiveLetters.test(trimmed)) {
    return {
      isValid: false,
      error:
        "Variables must be separated by operators (e.g., 'A + B' not 'AB')",
    };
  }

  // 4. Check all variables in formula are defined
  const usedVars = trimmed.match(/\b[A-Z]\b/g) || [];
  const definedVars = Object.keys(variables);
  const undefinedVars = usedVars.filter(v => !definedVars.includes(v));
  if (undefinedVars.length > 0) {
    return {
      isValid: false,
      error: `Undefined variable(s): ${[...new Set(undefinedVars)].join(', ')}`,
    };
  }

  // 5. Check for balanced parentheses
  let openParens = 0;
  for (const char of trimmed) {
    if (char === '(') openParens++;
    if (char === ')') openParens--;
    if (openParens < 0) {
      return { isValid: false, error: 'Unmatched closing parenthesis' };
    }
  }
  if (openParens > 0) {
    return { isValid: false, error: 'Unmatched opening parenthesis' };
  }

  // 6. Check for empty parentheses
  if (/\(\s*\)/.test(trimmed)) {
    return { isValid: false, error: 'Empty parentheses found' };
  }

  // 7. Check for consecutive operators (except negative sign after operator/parenthesis)
  if (/[+\/*]{2,}/.test(trimmed) || /--/.test(trimmed)) {
    return { isValid: false, error: 'Consecutive operators are not allowed' };
  }

  // 8. Check for operators at start/end (except negative sign at start)
  if (/^[+\/*]/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Formula cannot start with an operator (except -)',
    };
  }
  if (/[+\-\/*]$/.test(trimmed)) {
    return { isValid: false, error: 'Formula cannot end with an operator' };
  }

  // 9. Check for invalid decimal numbers (multiple dots)
  const numbers = trimmed.match(/\d+\.?\d*/g) || [];
  for (const num of numbers) {
    if ((num.match(/\./g) || []).length > 1) {
      return { isValid: false, error: `Invalid number format: ${num}` };
    }
  }

  // 10. Check for standalone dots or invalid dot usage (dots not part of valid numbers)
  if (/(?<!\d)\.(?!\d)/.test(trimmed)) {
    return { isValid: false, error: 'Invalid decimal point usage' };
  }

  // 11. Check for division by zero literal
  if (/\/\s*0(?!\d)/.test(trimmed)) {
    return { isValid: false, error: 'Division by zero is not allowed' };
  }

  // 12. Check for operators without operands
  if (/[+\-\/*]\s*[+\/*]/.test(trimmed)) {
    return { isValid: false, error: 'Missing operand between operators' };
  }
  if (/\(\s*[+\/*]/.test(trimmed) || /[+\-\/*]\s*\)/.test(trimmed)) {
    return { isValid: false, error: 'Missing operand near parenthesis' };
  }

  return { isValid: true, error: null };
};
